import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';
import { Branch } from './branchApi';

export interface Worker {
  id: number;
  workerCode?: string;
  name: string;
  address?: string;
  workerAddress?: string; // API field name
  dateOfJoining: string; // ISO string
  contactNumber: string;
  emergencyContactNumber?: string | null;
  aadhar?: string;
  workerAadhar?: string | null; // API field name
  branch?: Branch;
  branchId?: number;
  department?: string;
  departmentId?: number | null;
  workArea?: string;
  workAreaId?: number;
  reportingManager?: string;
  reportingManagerUserId?: number;
  workerManagerId?: number | null; // API field name
  shift?: string;
  shiftId?: number | null; // API field name
  skillLevel?: string;
  isActive?: boolean;
  createdBy?: string | number | null;
  updatedBy?: string | number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkerCreateRequest {
  workerCode?: string; // generated server-side, optional on create (not part of WorkerDto)
  name: string;
  // Legacy/local fields used by the form; mapped to WorkerDto fields in the API service
  address: string;
  dateOfJoining: string; // ISO / yyyy-MM-dd
  contactNumber: string;
  emergencyContactNumber: string;
  aadhar: string;
  branchId?: number;
  // IDs for related masters; mapped into WorkerDto
  workAreaId?: number;
  departmentId?: number;
  shiftId?: number;
  department?: string;
  workArea?: string;
  reportingManager?: string;
  reportingManagerUserId?: number;
  shift?: string;
  skillLevel?: string;
  isActive?: boolean;
}

export interface WorkerUpdateRequest extends WorkerCreateRequest {
  id: number;
}

class WorkerApiService extends BaseApiService {
  // Map front-end form data to backend WorkerDto payload
  private toWorkerDto(data: WorkerCreateRequest | WorkerUpdateRequest): any {
    const base: any = {
      // WorkerDto fields
      id: (data as any).id ?? undefined,
      name: data.name,
      workAreaId: data.workAreaId ?? (data as any).workAreaId ?? null,
      branchId: data.branchId ?? null,
      departmentId: data.departmentId ?? (data as any).departmentId ?? null,
      shiftId: data.shiftId ?? (data as any).shiftId ?? null,
      workerAddress: (data as any).workerAddress ?? (data as any).address ?? '',
      dateOfJoining: data.dateOfJoining, // backend expects LocalDate
      contactNumber: data.contactNumber,
      emergencyContactNumber: data.emergencyContactNumber || null,
      workerAadhar: (data as any).workerAadhar ?? (data as any).aadhar ?? null,
      workerManagerId: (data as any).workerManagerId ?? (data as any).reportingManagerUserId ?? null,
      isActive: data.isActive ?? true,
      createdBy: (data as any).createdBy ?? null,
      updatedBy: (data as any).updatedBy ?? null,
    };

    return base;
  }
  async getWorkers(branchId?: number): Promise<Worker[]> {
    try {
      const response = await this.makeRequest<ApiResponse<Worker[]>>(
        API_ENDPOINTS.WORKERS.LIST(branchId),
        { method: HTTP_METHODS.GET }
      );
      if (response.success) return response.data;
      throw new Error(response.message || 'Failed to fetch workers');
    } catch (error) {
      // console.error('Error fetching workers:', error);
      toast.error('Failed to fetch workers');
      throw error;
    }
  }

  async getWorkerById(id: string): Promise<Worker> {
    try {
      const response = await this.makeRequest<ApiResponse<Worker>>(
        API_ENDPOINTS.WORKERS.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      if (response.success) return response.data;
      throw new Error(response.message || 'Failed to fetch worker');
    } catch (error) {
      // console.error('Error fetching worker:', error);
      toast.error('Failed to fetch worker details');
      throw error;
    }
  }

  async createWorker(data: WorkerCreateRequest): Promise<Worker> {
    try {
      const payload = this.toWorkerDto(data);
      const response = await this.makeRequest<ApiResponse<Worker>>(
        API_ENDPOINTS.WORKERS.CREATE,
        { method: HTTP_METHODS.POST, body: JSON.stringify(payload) }
      );
      if (response.success) {
        toast.success('Worker created successfully');
        return response.data;
      }
      throw new Error(response.message || 'Failed to create worker');
    } catch (error) {
      // console.error('Error creating worker:', error);
      toast.error('Failed to create worker');
      throw error;
    }
  }

  async updateWorker(id: string, data: WorkerUpdateRequest): Promise<Worker> {
    try {
      const payload = this.toWorkerDto(data);
      const response = await this.makeRequest<ApiResponse<Worker>>(
        API_ENDPOINTS.WORKERS.UPDATE(id),
        { method: HTTP_METHODS.PUT, body: JSON.stringify(payload) }
      );
      if (response.success) {
        toast.success('Worker updated successfully');
        return response.data;
      }
      throw new Error(response.message || 'Failed to update worker');
    } catch (error) {
      // console.error('Error updating worker:', error);
      toast.error('Failed to update worker');
      throw error;
    }
  }

  async deleteWorker(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.WORKERS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      if (response.success) {
        toast.success('Worker deleted successfully');
        return;
      }
      throw new Error(response.message || 'Failed to delete worker');
    } catch (error) {
      // console.error('Error deleting worker:', error);
      toast.error('Failed to delete worker');
      throw error;
    }
  }

  async searchWorkers(q: string): Promise<Worker[]> {
    try {
      const response = await this.makeRequest<ApiResponse<Worker[]>>(
        `${API_ENDPOINTS.WORKERS.SEARCH}?q=${encodeURIComponent(q)}`,
        { method: HTTP_METHODS.GET }
      );
      if (response.success) return response.data;
      throw new Error(response.message || 'Failed to search workers');
    } catch (error) {
      // console.error('Error searching workers:', error);
      toast.error('Failed to search workers');
      throw error;
    }
  }

  async generateWorkerCode(): Promise<string> {
    try {
      const response = await this.makeRequest<ApiResponse<{ code: string }>>(
        API_ENDPOINTS.WORKERS.GENERATE_CODE,
        { method: HTTP_METHODS.GET }
      );
      if (response.success && response.data?.code) return response.data.code;
      throw new Error(response.message || 'Failed to generate worker code');
    } catch (error) {
      // console.warn('Generate code endpoint unavailable, falling back to client-side generation.');
      // Fallback: client-side temporary code
      const fallback = `WKR-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`;
      return fallback;
    }
  }

  async getWorkersByWorkAreaId(workAreaId: number, branchId?: number): Promise<Worker[]> {
    try {
      const response = await this.makeRequest<ApiResponse<Worker[]>>(
        API_ENDPOINTS.WORKERS.BY_WORK_AREA_ID(workAreaId.toString(), branchId),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) return response.data;
      throw new Error(response.message || 'Failed to fetch workers by work area');
    } catch (error) {
      // console.error('Error fetching workers by work area:', error);
      toast.error('Failed to fetch workers');
      throw error;
    }
  }
}

export const workerApiService = new WorkerApiService();


